import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { Input, Select } from 'antd';

import { Form, StyledModal, WrapperSelect } from 'common/components';

import MemberManagementEditForm from './MemberManagementEditForm';

import sortByKey from 'utils/sortByKey';
import { apiHandler } from 'utils/api';

import { useGlobalModal } from 'hooks';
import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { useGetMemberRoleList } from 'hooks/member/useGetMemberRoleList';

import * as memberManagementService from 'services/memberMaintenance';

import Messages from 'i18n/messages/maintenance';

const MemberManagementEditModal = (props) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const reloadPage = useDispatchReloadPage();

  const [loading, setLoading] = useState(false);

  const { checkModalVisible, closeModal, MODAL_NAMES } = useGlobalModal();
  const modalName = MODAL_NAMES.MEMBER_MAINTAIN.CREATE;
  const visible = checkModalVisible(modalName);

  const { memberRoleList } = useGetMemberRoleList({ enabled: visible });

  const formLayout = {
    wrapperCol: { span: 16 },
    labelCol: { span: 6 },
  };

  const onCancelHandler = () => {
    !loading && closeModal(modalName);
  };

  const onOkHandler = () => {
    form.validateFields().then((values) => {
      callApiEditMember(values);
    });
  };

  const initForm = () => {
    form.resetFields();
    setLoading(false);
  };

  const callApiEditMember = (values) => {
    if (!values) return;

    setLoading(true);

    const {
      memberName,
      accessChat,
      accessDigitalAssets,
      accessFTP,
      accessFolders,
      accessGDSN,
      accessInterops,
      accessMember,
      accessProducts,
      accessReporting,
      accessSyndication,
      accessWorkflow,
      ...restValues
    } = values;

    const params = {
      ...restValues,
      memberName: memberName?.trim(),
      canAccessProducts: accessProducts,
      canAccessMember: accessMember,
      canAccessDigitalAssets: accessDigitalAssets,
      canAccessFolders: accessFolders,
      canAccessSyndication: accessSyndication,
      canAccessGdsn: accessGDSN,
      canAccessFtp: accessFTP,
      canAccessInterops: accessInterops,
      canAccessChat: accessChat,
      canAccessReporting: accessReporting,
      canAccessWorkflow: accessWorkflow,
    };

    const successMessage = intl.formatMessage(Messages.createMemberSuccess);
    const errorMessage = intl.formatMessage(Messages.createMemberError);

    apiHandler({
      service: memberManagementService.createMemberManagement,
      params,
      successMessage,
      errorMessage,
      successCallback: editSuccessCallback,
      onFinally: hideLoading,
    });
  };

  const editSuccessCallback = () => {
    reloadPage();
    closeModal(modalName);
  };

  const hideLoading = () => {
    setLoading(false);
  };

  useEffect(() => {
    if (visible) initForm();
  }, [visible]);

  //* render
  const extraFields = (
    <>
      <Form.Item
        name='memberName'
        label={intl.formatMessage(Messages.memberName)}
        rules={[
          {
            required: true,
            message: intl.formatMessage(Messages.memberNameRequired),
          },
          {
            whitespace: true,
            message: intl.formatMessage(Messages.memberNameInvalid),
          },
        ]}
        {...formLayout}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name='memberRole'
        label={intl.formatMessage(Messages.memberRole)}
        rules={[
          {
            required: true,
            message: intl.formatMessage(Messages.memberRoleRequired),
          },
        ]}
        {...formLayout}
      >
        <WrapperSelect>
          {sortByKey(memberRoleList, 'roleName')?.map((item, index) => {
            return (
              <Select.Option key={index} value={item.roleName}>
                {item.roleName}
              </Select.Option>
            );
          })}
        </WrapperSelect>
      </Form.Item>

      <Form.Item
        name='ixOneId'
        label={intl.formatMessage(Messages.ixOneId)}
        rules={[
          {
            whitespace: true,
            message: intl.formatMessage(Messages.ixOneIdInvalid),
          },
        ]}
        {...formLayout}
      >
        <Input />
      </Form.Item>
    </>
  );

  const okBtnProp = { loading };
  const cancelBtnProp = { disabled: loading };

  return (
    <StyledModal
      visible={visible}
      closeable={false}
      onCancel={onCancelHandler}
      onOk={onOkHandler}
      okButtonProps={okBtnProp}
      cancelButtonProps={cancelBtnProp}
      okText={intl.formatMessage(Messages.editMemberModalOkBtn)}
      title={intl.formatMessage(Messages.createMemberModalTitle)}
    >
      <MemberManagementEditForm form={form} extraFields={extraFields} />
    </StyledModal>
  );
};

export default MemberManagementEditModal;
